/**
 * RudderStack Analytics
 *
 * Provides analytics tracking via RudderStack SDK.
 * Implements: page views, link clicks, document downloads, user identification, and reset.
 */

"use strict";

/**
 * Identify the user in RudderStack
 */
function identifyUser(userData) {
  if (!userData || !userData.user_id) {
    console.warn("[RudderStack] identifyUser called without valid user data");
    return;
  }

  window.rudderanalytics.identify(userData.user_id, {
    has_access: userData.has_access || false
  });

  console.log("[RudderStack] User identified:", userData.user_id, "has_access:", userData.has_access);
}

/**
 * Track a page view
 */
function pageView() {
  let title = document.title || "unknown";
  let url = window.location.href || "unknown";
  let path = window.location.pathname || "unknown";
  let search = window.location.search || "unknown";

  window.rudderanalytics.page(title, {
    title: title,
    url: url,
    path: path,
    search: search
  }, {
    integrations: { All: true }
  });

  console.log("[RudderStack] Page view:", title);
}

/**
 * Track a link click event
 */
function trackLink(anchor) {
  if (!(anchor instanceof HTMLAnchorElement)) {
    console.warn("[RudderStack] trackLink called with non-anchor element");
    return;
  }

  let rawHref = anchor.getAttribute("href") || "";
  let href = rawHref.indexOf("http") === 0 ? rawHref : window.location.origin + rawHref;

  window.rudderanalytics.track("Link Clicked", {
    target_url: href
  });

  console.log("[RudderStack] Link clicked:", href);
}

/**
 * Track a document download event
 */
function trackDownload(payload) {
  if (!payload) {
    console.warn("[RudderStack] trackDownload called without payload");
    return;
  }

  window.rudderanalytics.track("Document Downloaded", {
    document_id: payload.document_id || null,
    document_title: payload.document_title || payload.name || null,
    filename: payload.filename || payload.url || null,
    file_type_name: payload.file_type_name || payload.fileType || null,
    upload_type: payload.upload_type || null,
    user_acodes: payload.user_acodes || null,
    acodes: payload.acodes || null,
    file_extension: payload.file_extension || null
  });

  console.log("[RudderStack] Document downloaded:", payload.document_title || payload.name);
}

/**
 * Reset the user session in RudderStack
 */
function resetUser() {
  window.rudderanalytics.reset();
  console.log("[RudderStack] User reset");
}

/**
 * Set up automatic link click tracking
 */
function setupLinkTracking() {
  document.addEventListener("click", function(event) {
    let target = event.target;

    while (target && target.tagName !== "A") {
      target = target.parentElement;
    }

    if (!target) return;

    let href = target.getAttribute("href");
    if (!href || href.startsWith("javascript:") || href === "#") return;

    event.preventDefault();
    trackLink(target);

    setTimeout(function() {
      if (target.getAttribute("target") === "_blank") {
        window.open(href, "_blank");
      } else {
        window.location.href = href;
      }
    }, 100);
  });
}

/**
 * Set up automatic download tracking
 */
function setupDownloadTracking() {
  document.addEventListener("click", function(event) {
    let target = event.target;

    while (target && target.tagName !== "A") {
      target = target.parentElement;
    }

    if (!target) return;

    let href = target.getAttribute("href") || "";
    let hrefLower = href.toLowerCase();
    let isDownload = hrefLower.endsWith(".pdf");

    if (isDownload || target.hasAttribute("download") || target.classList.contains("magazine-download")) {
      event.preventDefault();

      let filename = href.split("/").pop() || href;
      let extension = filename.split(".").pop() || "";

      // Extract title from filename (replace dashes/underscores with spaces, keep extension)
      let titleFromPath = filename.replace(/[-_]/g, " ");

      trackDownload({
        document_title: titleFromPath || filename,
        filename: href.replace(/^.*\/\/[^\/]+/, ''),
        file_type_name: extension.toUpperCase(),
        file_extension: extension
      });

      setTimeout(function() {
        if (target.getAttribute("target") === "_blank") {
          window.open(href, "_blank");
        } else {
          window.location.href = href;
        }
      }, 100);
    }
  });
}

/**
 * Set up Piano user integration
 */
function setupPianoIntegration() {
  window.addEventListener("PianoUserLoaded", function(event) {
    let user = event.detail && event.detail.user;

    if (user) {
      let hasAccess = document.body.dataset.hasAccess === "true";
      identifyUser({
        user_id: user.uid || null,
        has_access: hasAccess
      });
    }
  });

  let logoutElements = document.querySelectorAll("#pp-subs-logout");
  logoutElements.forEach(function(element) {
    element.addEventListener("click", function() {
      resetUser();
    });
  });
}

/**
 * Initialize RudderStack tracking
 */
function initRudderStack() {
  if (typeof window.rudderanalytics !== "undefined" && window.rudderanalytics.ready) {
    window.rudderanalytics.ready(function() {
      console.log("[RudderStack] SDK ready");
      pageView();
      setupLinkTracking();
      setupDownloadTracking();
      setupPianoIntegration();
    });
  } else {
    let checkInterval = setInterval(function() {
      if (typeof window.rudderanalytics !== "undefined" && window.rudderanalytics.ready) {
        clearInterval(checkInterval);
        window.rudderanalytics.ready(function() {
          console.log("[RudderStack] SDK ready (delayed)");
          pageView();
          setupLinkTracking();
          setupDownloadTracking();
          setupPianoIntegration();
        });
      }
    }, 100);

    setTimeout(function() {
      clearInterval(checkInterval);
    }, 10000);
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRudderStack);
} else {
  initRudderStack();
}
